import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import EnquiryDialog from "./EnquiryDialog";

export const yachtTestId = (yacht) =>
  `yacht-card-${yacht.brand_slug}-${yacht.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

export default function YachtCard({ yacht, brandLabel, index, span = "", compact = false }) {
  const [open, setOpen] = useState(false);
  const testId = yachtTestId(yacht);
  const brandName = brandLabel.split(" · ")[0];
  const detailHref = `/yachts/${yacht.brand_slug}/${yacht.slug || ""}`;

  return (
    <>
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className={`group flex flex-col border border-navy-ink/10 bg-navy-light/40 ${span}`}
      data-testid={testId}
    >
      <Link to={detailHref} className="relative block overflow-hidden" data-testid={`${testId}-image-link`}>
        <img
          src={resolveImage(yacht.image)}
          alt={yacht.name}
          loading="lazy"
          className="aspect-[16/9] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-transparent" />
        <span className="absolute left-6 top-6 rounded-sm bg-black/35 px-2.5 py-1 text-[10px] uppercase tracking-[0.35em] text-white backdrop-blur-sm">
          {String(index + 1).padStart(2, "0")}
        </span>
        {yacht.category && (
          <span className="absolute right-6 top-6 rounded-sm bg-white/80 px-2.5 py-1 text-[10px] uppercase tracking-[0.3em] text-navy-ink backdrop-blur-sm" data-testid={`${testId}-category`}>
            {yacht.category}
          </span>
        )}
      </Link>
      <div className={`flex flex-1 flex-col ${compact ? "p-6 lg:p-7" : "p-8 lg:p-10"}`}>
        <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{brandLabel}</p>
        <Link to={detailHref} data-testid={`${testId}-name`} className="mt-3 block transition-colors duration-300 hover:text-gold">
          <h3 className={`font-serif font-light text-navy-ink ${compact ? "text-2xl" : "text-3xl lg:text-4xl"}`}>{yacht.name}</h3>
        </Link>
        {yacht.tagline && !compact && <p className="mt-3 text-sm font-light leading-relaxed text-mist">{yacht.tagline}</p>}
        {compact && yacht.specs?.length > 0 && (
          <div className="mt-5 grid grid-cols-2 border-t border-navy-ink/10">
            {yacht.specs.slice(0, 2).map((s) => (
              <div key={s.label} className="border-r border-navy-ink/10 py-3 pr-3 last:border-r-0">
                <p className="text-[9px] uppercase tracking-[0.25em] text-mist">{s.label}</p>
                <p className="mt-1 font-serif text-base text-navy-ink/90">{s.value}</p>
              </div>
            ))}
          </div>
        )}
        {!compact && (
          <div className="mt-8 grid grid-cols-2 border-t border-navy-ink/10 sm:grid-cols-4">
            {yacht.specs.map((s) => (
              <div key={s.label} className="border-r border-navy-ink/10 py-5 pr-4 last:border-r-0 [&:nth-child(2)]:border-r-0 sm:[&:nth-child(2)]:border-r">
                <p className="text-[9px] uppercase tracking-[0.25em] text-mist">{s.label}</p>
                <p className="mt-2 font-serif text-lg text-navy-ink/90">{s.value}</p>
              </div>
            ))}
          </div>
        )}
        <div className={`mt-auto flex flex-wrap items-center justify-between gap-4 ${compact ? "pt-5" : "pt-8"}`}>
          <Link
            to={detailHref}
            data-testid={`${testId}-details`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-navy-ink transition-colors duration-300 hover:text-gold"
          >
            View details <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <button
            onClick={() => setOpen(true)}
            data-testid={`${testId}-enquire`}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-gold transition-colors duration-300 hover:text-gold-light"
          >
            {compact ? "Enquire" : "Enquire about this yacht"}
            <ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </motion.article>
    <EnquiryDialog open={open} onClose={() => setOpen(false)} yacht={yacht} brandName={brandName} />
    </>
  );
}
